import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import type { BackendStructuredFilterGroupRequest } from "@/queries/request-log-query";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface StartSmartTableRequestImportResponse {
  success: boolean;
  operation_id: string;
  message: string;
}

interface SmartTableRequestImportBasePayload {
  operation_id?: string;
  variables_to_parse?: string[];
  include_fields?: string[] | null;
  limit?: number;
  anchor_column_id?: string;
}

export interface SmartTableRequestImportByIdsPayload extends SmartTableRequestImportBasePayload {
  request_log_ids: number[];
}

export interface SmartTableRequestImportByFilterPayload extends SmartTableRequestImportBasePayload {
  filter_group: BackendStructuredFilterGroupRequest;
  q?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export type SmartTableRequestImportPayload =
  | SmartTableRequestImportByIdsPayload
  | SmartTableRequestImportByFilterPayload;

interface StartSmartTableRequestImportArgs {
  tableId: string;
  sheetId: string;
  payload: SmartTableRequestImportPayload;
}

export const useStartSmartTableRequestImport = () => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);

  return useMutation<
    StartSmartTableRequestImportResponse,
    unknown,
    StartSmartTableRequestImportArgs
  >({
    mutationFn: async ({ tableId, sheetId, payload }) => {
      const response = await fetch(ENDPOINTS.smart_table_request_imports(wid, tableId, sheetId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });

      return (await handleResponse(response)) as StartSmartTableRequestImportResponse;
    },
  });
};
